/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useReactToPrint } from 'react-to-print';
import PropTypes from 'prop-types';

// @mui material components
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';

// Material Dashboard 2 PRO React components

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import './PointOfSale.css';
import MDSnackbar from 'components/MDSnackbar';

const initAttr = {
  id: '',
  code: '',
  remark: '',
  type_id: '',
  customer_id: '',
  quotation_id: '',
  vat_id: '2',
  status: 'edit',
  branch_receive: '',
  credit_day: '',
  credit_money: '',
  print_url: '',
  print_no: '0',
  detail_no: '0',
  created_at: '',
  updated_at: '',
  print: '',
  quotation: '',
  payment_type: 'CASH',
  shipping_id: '',
  time_shipping: '00:00',
  car_id: '',
  brandsend_id: '',
  printso_no: '',
  address: '',
  address2: '',
  printinvoice_no: '',
  discount_unit: 'Y',
  date_record: Func.dateNow(),
  date_due: Func.dateNow(),
  date_shipping: Func.dateNow(),
};

const PosPrintDrop = forwardRef(({ parent }, ref) => {
  const mod = 'pos_drop';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef(null);
  const promiseResolveRef = useRef(null);
  const [notify, setNotify] = useState(null);

  const quantityDisplay = (num) => {
    const fl = Math.floor(num);
    if (fl === num) return Func.numberDisplay(num, 0);
    return Func.numberDisplay(num);
  };

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onBeforeGetContent: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setIsPrinting(true);
      });
    },
    onAfterPrint: () => {
      promiseResolveRef.current = null;
      setIsPrinting(false);
    },
  });

  const print = (inp) => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/print`, app.token, {
      id: inp,
      lang: app.lang,
    })
      .then((result) => {
        if (result.status === 'ok') {
          setAttr(result.data);
          setList(result.list);
          handlePrint();
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderHeader = () => {
    return (
      <table width='100%'>
        <tbody>
          <tr>
            <td align='center'>
              {attr.image === '' ? null : <img src={attr.image} alt='' width='130' />}
            </td>
          </tr>
          <tr>
            <td align='center'>{attr.branch_name}</td>
          </tr>
          <tr>
            <td align='center'>
              <div className='title'>ใบดรอปเงิน</div>
            </td>
          </tr>
          <tr>
            <td>
              <p>&nbsp;</p>
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderOrder = () => {
    return (
      <table width='100%' cellPadding={5}>
        <thead>
          <tr>
            <th align='left' width='10%'>
              QTYL
            </th>
            <th align='left'>ITEM</th>
            <th align='right' width='20%'>
              PRICE
            </th>
            <th align='right' width='20%'>
              TOTAL
            </th>
          </tr>
        </thead>
        <tbody>
          {list.map((item) => (
            <tr key={item.id}>
              <td align='center' valign='top'>
                {quantityDisplay(item.quantity)}
              </td>
              <td valign='top'>x {item.name}</td>
              <td align='right' valign='top'>
                &nbsp;
              </td>
              <td align='right' valign='top'>
                {Func.numberDisplay(item.total, 2)}
              </td>
            </tr>
          ))}
          <tr>
            <td>&nbsp;</td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderTotal = () => {
    return (
      <table width='100%'>
        <thead>
          <tr>
            <th align='left' className='h2'>
              <h3>รวมนำส่ง</h3>
            </th>
            <th align='right' className='h2'>
              <h3>{Func.numberDisplay(attr.total, 2)}</h3>
            </th>
          </tr>
        </thead>
      </table>
    );
  };

  const renderFooter = () => {
    return (
      <table width='100%'>
        <tbody>
          <tr>
            <td colSpan={2}>Cashier : {attr.user_name}</td>
          </tr>
          <tr>
            <td>{attr.code}</td>
            <td>{Func.dateTimeDisplay(attr.today)}</td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderPrint = () => {
    return (
      <div style={{ display: 'none' }}>
        <div ref={printRef} className='slip'>
          {renderHeader()}
          {attr.status === 'photo' ? renderOrder() : null}
          {attr.status === 'photo' ? renderTotal() : null}
          {renderFooter()}
          <br />.
        </div>
      </div>
    );
  };

  useImperativeHandle(ref, () => {
    return {
      print,
    };
  });

  return (
    <div>
      {renderLoading()}
      {renderPrint()}
      {notify}
    </div>
  );
});

PosPrintDrop.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
};

export default PosPrintDrop;
